import { customInstance } from "../axios-instance";
import { API_CONFIG } from "@/constants/api";
import type {
  TransMasterDto,
  SupplementDataDto,
  CampaignRequestDto,
  CampaignResponseDto,
  TransMasterFilter,
  LlmModelDto,
  GetAllSupplementDataParams,
  GetAllCampaignsParams,
  ExchangeToken200,
  S3ListResponse,
  KbInfoResponse,
  ComplianceCountryDto,
  ComplianceCountryCreateRequest,
  ComplianceCountryUpdateRequest,
  RoleDto,
  AuthorityDto,
  RoleCreateRequest,
  RoleUpdateRequest,
  ConstantMessageDto,
  ConstantMessageUpdateRequest,
  ConstantMessageCreateRequest,
} from "./types";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

// Trans Master APIs
export const getTransMasterById = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<TransMasterDto>(
    {
      url: `${API_CONFIG.ENDPOINTS.TRANS_MASTER}/${id}`,
      method: "GET",
      signal,
    },
    options
  );
};

export const updateTransMasterById = (
  id: number,
  transMasterDto: TransMasterDto,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<null>(
    {
      url: `${API_CONFIG.ENDPOINTS.TRANS_MASTER}/${id}`,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: transMasterDto,
    },
    options
  );
};

export const deleteTransMasterById = (
  id: number,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<null>(
    {
      url: `${API_CONFIG.ENDPOINTS.TRANS_MASTER}/${id}`,
      method: "DELETE",
    },
    options
  );
};

export const filterTransMaster = (
  transMasterFilter: TransMasterFilter,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<TransMasterDto[]>(
    {
      url: API_CONFIG.ENDPOINTS.TRANS_MASTER_FILTER,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: transMasterFilter,
    },
    options
  );
};

export const updateTransMasterAction = (
  id: number,
  action: string,
  data: unknown,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<null>(
    {
      url: `${API_CONFIG.ENDPOINTS.TRANS_MASTER}/${id}/action/${action}`,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data,
    },
    options
  );
};

// Supplement Data APIs
export const getAllSupplementData = (
  params?: GetAllSupplementDataParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SupplementDataDto[]>(
    {
      url: API_CONFIG.ENDPOINTS.SUPP_DATA,
      method: "GET",
      params,
      signal,
    },
    options
  );
};

export const updateSupplementData = (
  supplementDataDto: SupplementDataDto,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<null>(
    {
      url: API_CONFIG.ENDPOINTS.SUPP_DATA,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: supplementDataDto,
    },
    options
  );
};

export const createSupplementData = (
  supplementDataDto: SupplementDataDto,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<SupplementDataDto>(
    {
      url: API_CONFIG.ENDPOINTS.SUPP_DATA,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: supplementDataDto,
    },
    options
  );
};

export const deleteSupplementData = (
  id: number,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<null>(
    {
      url: `${API_CONFIG.ENDPOINTS.SUPP_DATA}/${id}`,
      method: "DELETE",
    },
    options
  );
};

export const getSupplementTypes = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<string[]>(
    {
      url: API_CONFIG.ENDPOINTS.SUPP_DATA_TYPES,
      method: "GET",
      signal,
    },
    options
  );
};

// Campaign APIs
export const getAllCampaigns = (
  params?: GetAllCampaignsParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<CampaignResponseDto[]>(
    {
      url: API_CONFIG.ENDPOINTS.CAMPAIGN,
      method: "GET",
      params,
      signal,
    },
    options
  );
};

export const getCampaignById = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<CampaignRequestDto>(
    {
      url: `${API_CONFIG.ENDPOINTS.CAMPAIGN}/${id}`,
      method: "GET",
      signal,
    },
    options
  );
};

export const updateCampaign = (
  campaignRequestDto: CampaignRequestDto,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<null>(
    {
      url: API_CONFIG.ENDPOINTS.CAMPAIGN,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: campaignRequestDto,
    },
    options
  );
};

export const createCampaign = (
  campaignRequestDto: CampaignRequestDto,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<CampaignResponseDto>(
    {
      url: API_CONFIG.ENDPOINTS.CAMPAIGN,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: campaignRequestDto,
    },
    options
  );
};

export const deleteCampaign = (
  id: number,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<null>(
    {
      url: `${API_CONFIG.ENDPOINTS.CAMPAIGN}/${id}`,
      method: "DELETE",
    },
    options
  );
};

// LLM Model APIs
export const getAllLlmModels = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<LlmModelDto[]>(
    {
      url: API_CONFIG.ENDPOINTS.LLM_MODEL,
      method: "GET",
      signal,
    },
    options
  );
};

// Sender Persona APIs
export const getAllSenderPersonas = (
  params?: { name?: string; agentType?: string },
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<any[]>(
    {
      url: API_CONFIG.ENDPOINTS.SENDER_PERSONA,
      method: "GET",
      params,
      signal,
    },
    options
  );
};

// Analytics APIs
export const getTransactionTracking = (
  data: {
    agentType: string;
    fromDate?: string;
    toDate?: string;
    correlationId?: string;
    externalCd?: string;
    orderBy?: string;
    sortDirection?: string;
    minResponseTime?: number;
  },
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<any>(
    {
      url: `${API_CONFIG.ENDPOINTS.ANALYTICS_TRANSACTION_URL}/transaction-history`,
      method: "POST",
      data,
    },
    options
  );
};

export const exportTransactionHistoryCSV = (
  data: {
    agentType: string;
    fromDate?: string;
    toDate?: string;
    correlationId?: string;
    externalCd?: string;
    orderBy?: string;
    sortDirection?: string;
    minResponseTime?: number;
  },
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<Blob>(
    {
      url: `${API_CONFIG.ENDPOINTS.ANALYTICS_TRANSACTION_URL}/transaction-history/export/csv`,
      method: "POST",
      data,
      responseType: "blob",
    },
    options
  );
};

// Telemetry APIs
export const getTransactionTelemetry = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<any>(
    {
      url: `${API_CONFIG.ENDPOINTS.ANALYTICS_TRANSACTION_URL}/transaction-history/telemetry/${id}`,
      method: "GET",
      signal,
    },
    options
  );
};

// Conversation APIs
export const getTransactionConversation = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<any>(
    {
      url: `${API_CONFIG.ENDPOINTS.ANALYTICS_TRANSACTION_URL}/transaction-history/conversation/${id}`,
      method: "GET",
      signal,
    },
    options
  );
};

// Failure Transactions APIs
export const getFailureTransactions = (
  data: {
    agentType: string;
    transType?: string;
    correlationId?: string;
    fromDate?: string;
    toDate?: string;
    status?: string[];
  },
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<any>(
    {
      url: `${API_CONFIG.ENDPOINTS.ANALYTICS_TRANSACTION_URL}/transaction-retry-queue`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(options?.headers || {})
      },
      data,
    },
    options
  );
};

export const getFailureTransactionPayload = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<any>(
    {
      url: `${API_CONFIG.ENDPOINTS.ANALYTICS_TRANSACTION_URL}/transaction-retry-queue/payload/${id}`,
      method: "GET",
      signal,
    },
    options
  );
};

export const exportFailureTransactionsCSV = (
  data: {
    agentType: string;
    transType?: string;
    correlationId?: string;
    fromDate?: string;
    toDate?: string;
    status?: string[];
  },
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<Blob>(
    {
      url: `${API_CONFIG.ENDPOINTS.ANALYTICS_TRANSACTION_URL}/transaction-retry-queue/export/csv`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data,
      responseType: "blob",
    },
    options
  );
};

export const retryFailureTransaction = (
  id: number,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<{ message: string }>(
    {
      url: `${API_CONFIG.ENDPOINTS.ANALYTICS_TRANSACTION_URL}/transaction-retry-queue/next-run-date/id/${id}`,
      method: "POST",
    },
    options
  );
};

// Auth APIs
export const exchangeToken = (
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ExchangeToken200>(
    {
      url: API_CONFIG.ENDPOINTS.AUTH_EXCHANGE,
      method: "POST",
    },
    options
  );
};

// Knowledge Base APIs
export const getConfigDetails = (
  key: string,
  agentType: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<any[]>(
    {
      url: `${API_CONFIG.ENDPOINTS.INTEGRATION_CONFIG}/key/${key}/type/${agentType}`,
      method: "GET",
      signal,
    },
    options
  );
};

export const getKbInfo = (
  filter: { agentType: string; kbType: string },
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<any[]>(
    {
      url: `${API_CONFIG.ENDPOINTS.KNOWLEDGE_BASE_INFO}/search`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: filter,
      signal,
    },
    options
  );
};

export const getKbInfoById = (
  id: string | number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<KbInfoResponse>(
    {
      url: `${API_CONFIG.ENDPOINTS.KNOWLEDGE_BASE_INFO}/id/${id}`,
      method: "GET",
      signal,
    },
    options
  );
};

export const updateKbInfo = (
  kbInfoData: {
    id?: number | null;
    agentType: string;
    kbId: string;
    bucketName: string;
    kbType: string;
    kbTypeValue: string;
    kbTypeId?: string;
    dataSourceId: string;
    metadataSchema: string;
    key?: string;
  },
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<null>(
    {
      url: API_CONFIG.ENDPOINTS.KNOWLEDGE_BASE_INFO,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: kbInfoData,
    },
    options
  );
};

// S3 File Management APIs
export const listS3Files = (
  s3Url: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<S3ListResponse>(
    {
      url: `${API_CONFIG.ENDPOINTS.S3_URL}/list`,
      method: "GET",
      params: { s3Url },
      signal,
    },
    options
  );
};

export const downloadS3File = (
  s3Url: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<Blob>(
    {
      url: `${API_CONFIG.ENDPOINTS.S3_URL}/download`,
      method: "GET",
      params: { s3Url },
      responseType: "blob",
    },
    options
  );
};

export const uploadS3File = (
  file: File,
  bucketName: string,
  prefix?: string,
  metadata?: Record<string, any>,
  kbId?: string,
  dataSourceId?: string,
  options?: SecondParameter<typeof customInstance>
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("bucketName", bucketName);

  // Construct prefix as 'fileName' for root folder or 'folderName/fileName' for subfolders
  const fileName = file.name;
  const prefixToSend = !prefix || prefix.trim() === ""
    ? `${fileName}`
    : `${prefix}/${fileName}`;
  formData.append("prefix", prefixToSend);

  // Add metadata if provided
  if (metadata && Object.keys(metadata).length > 0) {
    formData.append("metadata", JSON.stringify(metadata));
  }

  // Add kbId and dataSourceId if provided
  if (kbId) {
    formData.append("kbId", kbId);
  }
  if (dataSourceId) {
    formData.append("dataSourceId", dataSourceId);
  }

  return customInstance<any>(
    {
      url: `${API_CONFIG.ENDPOINTS.S3_URL}/upload`,
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
    options
  );
};

export const createS3Folder = (
  bucketName: string,
  folderName: string,
  prefix?: string,
  options?: SecondParameter<typeof customInstance>
) => {
  // Construct prefix as empty string for root folder or 'folderPath' for subfolders
  const prefixToSend = !prefix || prefix.trim() === "" ? "/" : prefix;

  return customInstance<any>(
    {
      url: `${API_CONFIG.ENDPOINTS.S3_URL}/create-folder`,
      method: "POST",
      data: {
        bucketName,
        prefix: prefixToSend,
        folderName
      },
    },
    options
  );
};

export const createStructuredS3Folder = (
  bucketName: string,
  folderName: string,
  prefix?: string,
  key?: string,
  parentKey?: string,
  id?: string,
  metadata?: Record<string, any>,
  agentType?: string,
  options?: SecondParameter<typeof customInstance>
) => {
  const payload: any = {
    bucketName,
    folderName
  };

  // Only include prefix if it's provided
  if (prefix) {
    payload.prefix = prefix;
  }

  if (key) {
    payload.key = key;
  }

  if (parentKey) {
    payload.parentKey = parentKey;
  }

  if (id) {
    payload.id = id;
  }

  if (agentType) {
    payload.agentType = agentType;
  }

  if (metadata && Object.keys(metadata).length > 0) {
    payload.metadata = metadata;
  }

  return customInstance<any>(
    {
      url: `${API_CONFIG.ENDPOINTS.S3_URL}/create-folder`,
      method: "POST",
      data: payload,
    },
    options
  );
};

export const deleteS3Item = (
  s3Url: string,
  type: "file" | "folder",
  kbId?: string,
  dataSourceId?: string,
  options?: SecondParameter<typeof customInstance>
) => {
  const params: any = { s3Url, type };

  // Add kbId and dataSourceId if provided
  if (kbId) {
    params.kbId = kbId;
  }
  if (dataSourceId) {
    params.dataSourceId = dataSourceId;
  }

  return customInstance<any>(
    {
      url: `${API_CONFIG.ENDPOINTS.S3_URL}/delete`,
      method: "DELETE",
      params,
    },
    options
  );
};

// TPMO APIs
export const getTpmoTransMasters = (
  filter: {
    agentType: string;
    campaignName?: string;
    externalCd?: string;
    statuses?: string[];
  },
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<any[]>(
    {
      url: API_CONFIG.ENDPOINTS.TRANS_MASTER_FILTER,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: filter,
      signal,
    },
    options
  );
};

export const getTpmoTransMasterById = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<any>(
    {
      url: `${API_CONFIG.ENDPOINTS.TRANS_MASTER}/${id}`,
      method: "GET",
      signal,
    },
    options
  );
};

export const startTpmoJob = (
  id: number,
  data: {
    appointmentTime: string;
    phoneNo: string;
    timezone: string;
    countryCode: string;
  },
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<any>(
    {
      url: API_CONFIG.ENDPOINTS.TEMP_TPMO_START_JOB,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: {
        id,
        appointmentTime: data.appointmentTime,
        timezone: data.timezone,
        phoneNo: data.phoneNo,
        countryCode: data.countryCode,
      },
    },
    options
  );
};

export const updateTpmoPhoneNo = (
  id: number,
  phoneNo: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<any>(
    {
      url: API_CONFIG.ENDPOINTS.TEMP_TPMO_UPDATE_PHONE,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: {
        id,
        phoneNo,
      },
    },
    options
  );
};

export const getCountriesList = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<Array<{
    id: number;
    countryCode: string;
    countryName: string;
    isdCode: string;
    startWorkingHour: string;
    endWorkingHour: string;
  }>>(
    {
      url: API_CONFIG.ENDPOINTS.COUNTRIES_LIST,
      method: "GET",
      signal,
    },
    options
  );
};

// Compliance & Quiet Hours APIs
export const getAllComplianceCountries = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<ComplianceCountryDto[]>(
    {
      url: API_CONFIG.ENDPOINTS.COMPLIANCE_COUNTRIES,
      method: "GET",
      signal,
    },
    options
  );
};

export const getComplianceCountryById = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<ComplianceCountryDto>(
    {
      url: `${API_CONFIG.ENDPOINTS.COMPLIANCE_COUNTRIES}/${id}`,
      method: "GET",
      signal,
    },
    options
  );
};

export const createComplianceCountry = (
  countryData: ComplianceCountryCreateRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ComplianceCountryDto>(
    {
      url: API_CONFIG.ENDPOINTS.COMPLIANCE_COUNTRIES,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: countryData,
    },
    options
  );
};

export const updateComplianceCountry = (
  id: number,
  countryData: ComplianceCountryUpdateRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ComplianceCountryDto>(
    {
      url: `${API_CONFIG.ENDPOINTS.COMPLIANCE_COUNTRIES}/${id}`,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: countryData,
    },
    options
  );
};

export const deleteComplianceCountry = (
  id: number,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<null>(
    {
      url: `${API_CONFIG.ENDPOINTS.COMPLIANCE_COUNTRIES}/${id}`,
      method: "DELETE",
    },
    options
  );
};

// Role Management APIs
export const getAllRoles = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<RoleDto[]>(
    {
      url: API_CONFIG.ENDPOINTS.ADMIN_ROLES,
      method: "GET",
      signal,
    },
    options
  );
};

export const getRoleById = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<RoleDto>(
    {
      url: `${API_CONFIG.ENDPOINTS.ADMIN_ROLES}/${id}`,
      method: "GET",
      signal,
    },
    options
  );
};

export const createRole = (
  roleData: RoleCreateRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<RoleDto>(
    {
      url: API_CONFIG.ENDPOINTS.ADMIN_ROLES,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: roleData,
    },
    options
  );
};

export const updateRole = (
  id: number,
  roleData: RoleUpdateRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<RoleDto>(
    {
      url: `${API_CONFIG.ENDPOINTS.ADMIN_ROLES}/${id}`,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: roleData,
    },
    options
  );
};

export const deleteRole = (
  id: number,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<null>(
    {
      url: `${API_CONFIG.ENDPOINTS.ADMIN_ROLES}/${id}`,
      method: "DELETE",
    },
    options
  );
};

// Authority Management APIs
export const getAllAuthorities = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<AuthorityDto[]>(
    {
      url: API_CONFIG.ENDPOINTS.ADMIN_AUTHORITIES,
      method: "GET",
      signal,
    },
    options
  );
};

export const getAuthorityById = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<AuthorityDto>(
    {
      url: `${API_CONFIG.ENDPOINTS.ADMIN_AUTHORITIES}/${id}`,
      method: "GET",
      signal,
    },
    options
  );
};

// Role-Authority Mapping APIs
export const getRoleAuthorities = (
  roleId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<AuthorityDto[]>(
    {
      url: `${API_CONFIG.ENDPOINTS.ADMIN_ROLES}/${roleId}/authorities`,
      method: "GET",
      signal,
    },
    options
  );
};

export const assignAuthoritiesToRole = (
  roleId: number,
  authorityIds: number[],
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<null>(
    {
      url: `${API_CONFIG.ENDPOINTS.ADMIN_ROLES}/${roleId}/authorities`,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: authorityIds,
    },
    options
  );
};

export const getSorLookup = (
  agentType: string,
  type: string,
  lookupKey?: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
  searchValue?: string,
  parentType?: string
) => {
  const params: any = { agentType, type };
  if (lookupKey) {
    params.lookupKey = lookupKey;
  }
  if (searchValue) {
    params.searchKey = searchValue;
  }
  if (parentType) {
    params.parentType = parentType;
  }

  return customInstance<{ data: Array<{ id: string; value: string; name: string }> }>(
    {
      url: `${API_CONFIG.ENDPOINTS.SOR_LOOKUP}`,
      method: "GET",
      params,
      signal,
    },
    options
  );
};

// Constants Management APIs
export const getConstantMessages = (
  agentType: string,
  language: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<ConstantMessageDto[]>(
    {
      url: API_CONFIG.ENDPOINTS.ADMIN_CONSTANT_MESSAGES,
      method: "GET",
      params: { agentType, language },
      signal,
    },
    options
  );
};

export const updateConstantMessage = (
  data: ConstantMessageUpdateRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ConstantMessageDto>(
    {
      url: API_CONFIG.ENDPOINTS.ADMIN_CONSTANT_MESSAGES,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data,
    },
    options
  );
};

export const createConstantMessage = (
  data: ConstantMessageCreateRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ConstantMessageDto>(
    {
      url: API_CONFIG.ENDPOINTS.ADMIN_CONSTANT_MESSAGES,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data,
    },
    options
  );
};

